import axios, { CancelTokenSource } from 'axios'
import cn from 'classnames'
import { DEFAULT_PAGE } from 'components/Table/constants'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AddHealthSystemParams,
  AddHealthSystemPopupProps,
  AddHealthSystemRequestData,
  IRfpHealthSystem,
  IRfpHealthSystemWithHospitals
} from './types'
import { useDispatch } from 'react-redux'
import { useDebounce } from '../../../../hooks/hooks'
import { setLoading } from '../../../../redux/store/common/slice'
import { Popup } from '../../../../components/Popup'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { PLACEHOLDERS, BTN_TXT } from 'constants/txt'
import { Table } from '../../../../components/Table'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as ArrowDown16 } from '../../../../assets/svg/ArrowDown16.svg'
import { ReactComponent as ArrowRight16 } from '../../../../assets/svg/ArrowRight16.svg'
import { useRFPDetailsContext } from '../Providers/RFPDetailsContextProvider'
import { addHSToRfp, getHSList } from '../api'
import { useRFPDetailsPopupContext } from '../Providers/RFPDetailsPopupProvider'
import { Input } from 'components/Input'
import { columns } from './constants'

export const AddHealthSystemPopup = ({
  updateList,
  canSeeHealthSystem
}: AddHealthSystemPopupProps) => {
  const [totalNumber, setTotalNumber] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [firstLoad, setFirstLoad] = useState(true)
  const [sortParams, setSortParams] = useState<AddHealthSystemRequestData>()
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [tableData, setTableData] = useState<IRfpHealthSystemWithHospitals[]>(
    []
  )
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<IRfpHealthSystem[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const { data } = useRFPDetailsContext().state
  const { addHealthSystemPopup } = useRFPDetailsPopupContext()

  const dispatch = useDispatch()

  const debouncedSearch = useDebounce(searchValue, 500)

  const displayData = useMemo(
    () =>
      canSeeHealthSystem
        ? tableData
        : (tableData.flatMap(
            (hs) => hs.children
          ) as IRfpHealthSystemWithHospitals[]),
    [canSeeHealthSystem, tableData]
  )

  useEffect(() => {
    return () => {
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    if (addHealthSystemPopup.state.visible) {
      timeoutRef.current = setTimeout(
        () => {
          getTableData({
            ...sortParams,
            page: { ...DEFAULT_PAGE }
          })
          setPageInfo({ ...DEFAULT_PAGE })
        },
        firstLoad ? 0 : 500
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, addHealthSystemPopup.state.visible])

  const getTableData = async ({
    sortField,
    sortOrder,
    page
  }: AddHealthSystemRequestData) => {
    if (!data.uuid) {
      throw new Error('RFP ID not provided')
    }
    try {
      dispatch(setLoading(true))
      if (tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
      const params: AddHealthSystemParams = {}
      if (searchValue) {
        params.search = searchValue
      }
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ? page : pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      tableDataTokenRef.current = axios.CancelToken.source()
      const resp = await getHSList(data.uuid, {
        cancelToken: tableDataTokenRef.current?.token,
        params
      })
      setTableData(
        resp.data.results.map((i) => ({
          ...i,
          key: i.uuid,
          hospital_level: false,
          children: i.hospitals.map((hospital) => {
            return {
              ...hospital,
              key: hospital.uuid,
              hospital_level: true
            }
          })
        }))
      )
      setTotalNumber(resp.data.count)
      if (firstLoad) {
        setFirstLoad(false)
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      page
    })
    setPageInfo(page)
  }

  const onAddHSToCC = async () => {
    try {
      dispatch(setLoading(true))
      const multipleHS = selectedRowKeys.length > 1
      await addHSToRfp(
        data.uuid,
        {
          hospitals: selectedRows
            .filter((selectedRow) => selectedRow.hospital_level)
            .map((selectedRow) => selectedRow.uuid)
        },
        `Health System${multipleHS ? 's' : ''} ha${
          multipleHS ? 've' : 's'
        } been added to the rfp.`
      )
      onCancel()
      updateList()
    } catch (e) {
      dispatch(setLoading(false))
    }
  }

  const onCancel = () => {
    addHealthSystemPopup.actions.close()
    handleSelectRow([], [])
    setExpandedRowKeys([])
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      page
    })
    setPageInfo(page)
  }

  const handleSelectRow = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRows)
  }

  return (
    <Popup
      visible={addHealthSystemPopup.state.visible}
      onCancel={onCancel}
      width="100%"
      title={canSeeHealthSystem ? 'Add Health System' : 'Add Hospital'}
      className="add-health-system-popup"
      footer={
        <>
          <Button upperCase onClick={onCancel}>
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            disabled={!selectedRowKeys.length}
            onClick={onAddHSToCC}
            upperCase
          >
            {BTN_TXT.ADD}
          </Button>
        </>
      }
    >
      {!selectedRowKeys.length && (
        <Input
          className="add-health-system-popup__search allow-clear"
          propsInput={{
            allowClear: { clearIcon: <Close16 /> },
            placeholder: PLACEHOLDERS.SEARCH,
            prefix: <Search16 />,
            value: searchValue,
            onChange: (e) => setSearchValue(e.target.value)
          }}
        />
      )}
      <Table
        className={cn('mt-4 rfp-hs-list-table', {
          selectable: canSeeHealthSystem
        })}
        dataSource={displayData}
        pageSize={pageInfo.pageSize}
        pagination={{
          pageSize: pageInfo.pageSize,
          current: pageInfo.pageNumber,
          total: totalNumber
        }}
        columns={columns({
          showAmountOfHospitals: false,
          canSeeHealthSystem
        })}
        onChangePage={handleChangePageSize}
        onChange={handleTableChange}
        rowSelection={{
          selectedRowKeys,
          onChange: handleSelectRow,
          hideSelectAll: false,
          checkStrictly: false,
          getCheckboxProps: ({}) => {
            return {
              disabled: false
            }
          }
        }}
        expandable={
          canSeeHealthSystem
            ? {
                defaultExpandAllRows: false,
                columnWidth: 40,
                expandedRowKeys,
                onExpand: (expanded, record) => {
                  if (expanded) setExpandedRowKeys([record.uuid])
                  else setExpandedRowKeys([])
                },
                expandIcon: ({ expanded, onExpand, record, expandable }) =>
                  expandable ? (
                    expanded ? (
                      <div
                        className="expand-wrapper row gap-12 align-center"
                        onClick={(e) => onExpand(record, e)}
                      >
                        <ArrowDown16 />
                      </div>
                    ) : (
                      <div
                        className="expand-wrapper row gap-12 align-center"
                        onClick={(e) => onExpand(record, e)}
                      >
                        <ArrowRight16 />
                      </div>
                    )
                  ) : (
                    <></>
                  )
              }
            : undefined
        }
      />
    </Popup>
  )
}
